/**
 * Journey Intro Screen
 * Beautiful logo reveal animation before entering a journey.
 * Placeholder for future intro video.
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const JourneyIntroScreen = ({ 
  onComplete, 
  journeyTitle,
  gradient = ["#7C3AED", "#EC4899"],
  duration = 3000 // ms before auto-complete
}) => {
  const [phase, setPhase] = useState("logo"); // logo -> text -> fade

  useEffect(() => {
    // Phase 1: Logo reveal (0-1.5s)
    const textTimer = setTimeout(() => {
      setPhase("text");
    }, 1500);

    // Phase 2: Complete after duration
    const completeTimer = setTimeout(() => {
      setPhase("fade");
      setTimeout(onComplete, 500);
    }, duration);

    return () => {
      clearTimeout(textTimer);
      clearTimeout(completeTimer);
    };
  }, [duration, onComplete]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex flex-col items-center justify-center"
        style={{ background: "#0a0a0f" }}
      >
        {/* Ambient glow */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${gradient[0]}40, transparent 70%)`
          }}
        />

        {/* Logo */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ 
            scale: 1, 
            opacity: 1,
            y: phase === "text" ? -20 : 0
          }}
          transition={{ 
            duration: 0.8, 
            type: "spring",
            stiffness: 100
          }}
          className="relative z-10"
        >
          {/* Reclaim Logo - Simple elegant version */}
          <svg 
            width="120" 
            height="120" 
            viewBox="0 0 100 100" 
            className="mx-auto"
          >
            {/* Circle */}
            <motion.circle
              cx="50"
              cy="35"
              r="20"
              fill="none"
              stroke="url(#logoGradient)"
              strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
            />
            
            {/* Vertical line */}
            <motion.line
              x1="50"
              y1="55"
              x2="50"
              y2="85"
              stroke="url(#logoGradient)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeInOut" }}
            />
            
            {/* Horizontal line */}
            <motion.line
              x1="35"
              y1="70"
              x2="65"
              y2="70"
              stroke="url(#logoGradient)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8, ease: "easeInOut" }}
            />
            
            <defs>
              <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={gradient[0]} />
                <stop offset="100%" stopColor={gradient[1]} />
              </linearGradient>
            </defs>
          </svg>

          {/* RECLAIM text */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-4 text-center"
          >
            <span 
              className="text-2xl font-light tracking-[0.3em] bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`
              }}
            >
              RECLAIM
            </span>
          </motion.div>
        </motion.div>

        {/* Journey title - appears in phase 2 */}
        <AnimatePresence>
          {phase === "text" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="mt-8 text-center z-10"
            >
              <p className="text-white/40 text-sm tracking-wider uppercase mb-2">
                Beginning your journey
              </p>
              <h2 className="text-white text-xl font-light">
                {journeyTitle}
              </h2>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skip button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          onClick={onComplete}
          className="absolute bottom-12 text-white/30 text-sm hover:text-white/60 transition-colors"
        >
          Tap to continue
        </motion.button>

        {/* Subtle particle effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full"
              style={{
                background: gradient[i % 2],
                left: `${20 + i * 12}%`,
                top: "60%"
              }}
              animate={{
                y: [-20, -100],
                opacity: [0, 0.6, 0],
                scale: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeOut"
              }}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JourneyIntroScreen;
